import { isMockFidoToken } from "../Constants/webauthnMock";
import { SESSION_OWNER_TAB_ID_KEY } from "./sessionTabClaim";

/** Must match AuthContext sessionKey — read directly to avoid circular-import races. */
export const AUTH_SESSION_KEY = "@WalletBC";

/** Window event PrivateApp listens to for forced logout on expired/invalid session. */
export const AUTH_401_EVENT = "error-401";

let auth401DispatchLock = false;

/**
 * This tab was displaced (another tab claimed the session) or fully logged out.
 * getAuthToken must not re-seed from localStorage or the old tab would keep
 * sending the still-valid JWT after its UI logged out.
 */
let tabSessionRevoked = false;

export const markTabSessionRevoked = () => {
  tabSessionRevoked = true;
};

export const clearTabSessionRevoked = () => {
  tabSessionRevoked = false;
};

export const isTabSessionRevoked = () => tabSessionRevoked;

const readTokenFromJson = (raw) => {
  if (!raw) return "";
  try {
    const parsed = JSON.parse(raw);
    const token = parsed?.token;
    return typeof token === "string" && token.trim() ? token.trim() : "";
  } catch {
    return "";
  }
};

const safeGet = (storage, key) => {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (storage, key, value) => {
  try {
    storage.setItem(key, value);
  } catch {
    // Quota / private mode
  }
};

const safeRemove = (storage, key) => {
  try {
    storage.removeItem(key);
  } catch {
    // ignore
  }
};

/**
 * Tab-scoped session in sessionStorage; browser-scoped JWT in localStorage so a
 * duplicated tab (which may not copy sessionStorage) can hydrate.
 * Seeds sessionStorage from localStorage once per tab, unless this tab was
 * locally logged out.
 */
export const readAuthSessionJson = () => {
  if (tabSessionRevoked) {
    return safeGet(sessionStorage, AUTH_SESSION_KEY);
  }

  const fromTab = safeGet(sessionStorage, AUTH_SESSION_KEY);
  if (fromTab) {
    if (!safeGet(localStorage, AUTH_SESSION_KEY)) {
      safeSet(localStorage, AUTH_SESSION_KEY, fromTab);
    }
    return fromTab;
  }

  const fromBrowser = safeGet(localStorage, AUTH_SESSION_KEY);
  if (fromBrowser) {
    safeSet(sessionStorage, AUTH_SESSION_KEY, fromBrowser);
    return fromBrowser;
  }

  return null;
};

export const persistAuthSessionJson = (json, { browser = true } = {}) => {
  if (typeof json !== "string") return;
  safeSet(sessionStorage, AUTH_SESSION_KEY, json);
  if (browser && !tabSessionRevoked) {
    safeSet(localStorage, AUTH_SESSION_KEY, json);
  }
};

export const clearAuthSession = ({ browser = true } = {}) => {
  const tabJson = safeGet(sessionStorage, AUTH_SESSION_KEY);
  const tabToken = readTokenFromJson(tabJson);

  safeRemove(sessionStorage, AUTH_SESSION_KEY);

  if (!browser) return;

  const browserJson = safeGet(localStorage, AUTH_SESSION_KEY);
  const browserToken = readTokenFromJson(browserJson);
  // Another tab may already have written a newer JWT (re-login). Never wipe that.
  if (browserToken && tabToken && browserToken !== tabToken) {
    return;
  }

  safeRemove(localStorage, AUTH_SESSION_KEY);
  safeRemove(localStorage, SESSION_OWNER_TAB_ID_KEY);
};

const readPersistedAuthToken = () => {
  if (tabSessionRevoked) {
    return readTokenFromJson(safeGet(sessionStorage, AUTH_SESSION_KEY));
  }

  for (const storage of [sessionStorage, localStorage]) {
    const token = readTokenFromJson(safeGet(storage, AUTH_SESSION_KEY));
    if (token) return token;
  }

  return "";
};

/**
 * Reads the wallet JWT from persisted session storage.
 * Intentionally avoids importing AuthContext/useAuthStore — that circular import
 * (AuthContext → api → authToken → AuthContext) left the store empty at interceptor
 * time while api.defaults still had the token.
 */
export const getAuthToken = () => readPersistedAuthToken();

/** True when the current location is the public auth flow (avoid logout redirect loops). */
export const isOnAuthRoute = () => {
  if (typeof window === "undefined") return false;
  const path = window.location?.pathname || "";
  return path === "/auth" || path.startsWith("/auth/");
};

/** Auth endpoints that may legitimately return 401 without ending a session. */
export const isAuthCredentialRequest = (url = "") => {
  const path = String(url);
  return (
    path.includes("/v1/auth/login") ||
    path.includes("/v1/auth/verify") ||
    path.includes("/v1/auth/logout") ||
    path.includes("/v1/auth/refresh") ||
    path.includes("/v1/auth/otp/") ||
    path.includes("/v1/auth/first-access/") ||
    path.includes("/v1/auth/password-reset/") ||
    path.includes("/v1/oauth/webauthn/authenticate/")
  );
};

/** Anonymous login / recovery / first-access — do not attach a leftover JWT. */
const isAnonymousAuthFunnelRequest = (url = "") => {
  const path = String(url);
  return (
    path.includes("/v1/auth/login") ||
    path.includes("/v1/auth/otp/") ||
    path.includes("/v1/auth/first-access/") ||
    path.includes("/v1/auth/password-reset/") ||
    path.includes("/v1/oauth/webauthn/authenticate/")
  );
};

/**
 * Decodes JWT `exp` (seconds) → ms, or null if not a JWT / no exp.
 */
export const getJwtExpiryMs = (token) => {
  if (typeof token !== "string" || !token.includes(".")) return null;

  try {
    const payloadPart = token.split(".")[1];
    if (!payloadPart) return null;

    const normalized = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      "="
    );
    const payload = JSON.parse(atob(padded));
    if (typeof payload?.exp !== "number") return null;
    return payload.exp * 1000;
  } catch {
    return null;
  }
};

/** Client-side expiry check when the token carries a JWT `exp` claim. */
export const isAuthTokenExpired = (token, skewMs = 5_000) => {
  if (!token || isMockFidoToken(token)) return false;
  const expMs = getJwtExpiryMs(token);
  if (expMs == null) return false;
  return Date.now() >= expMs - skewMs;
};

/**
 * Notifies PrivateApp to clear session and redirect to login.
 * No-ops on auth routes, mock FIDO tokens, and while a dispatch is already in flight.
 */
export const dispatchAuthUnauthorized = () => {
  if (typeof window === "undefined") return;
  if (isOnAuthRoute()) return;

  const token = getAuthToken();
  if (isMockFidoToken(token)) return;
  if (auth401DispatchLock) return;

  auth401DispatchLock = true;
  window.dispatchEvent(new Event(AUTH_401_EVENT));
  window.setTimeout(() => {
    auth401DispatchLock = false;
  }, 2_000);
};

/**
 * Request-interceptor helper: if the stored JWT is past `exp`, fire session disconnect
 * and throw so the call does not proceed with a dead token.
 * On `/auth/*`, clear the stale session quietly so public branding/login bootstrap
 * is not blocked by AuthTokenExpiredError (partner hosts would spin forever).
 */
export const assertAuthTokenNotExpired = () => {
  const token = getAuthToken();
  if (!token || !isAuthTokenExpired(token)) return;

  if (isOnAuthRoute()) {
    clearAuthSession({ browser: true });
    clearTabSessionRevoked();
    return;
  }

  dispatchAuthUnauthorized();
  const error = new Error("Session expired");
  error.name = "AuthTokenExpiredError";
  error.isAuthExpired = true;
  throw error;
};

/** Attaches Bearer auth to an axios request config when a token is available. */
export const applyBearerAuth = (config) => {
  if (!config.headers) {
    config.headers = {};
  }

  // Strip leftover JWT from anonymous recovery / login / first-access calls.
  if (isAnonymousAuthFunnelRequest(config?.url)) {
    delete config.headers.Authorization;
    if (config.headers.common) {
      delete config.headers.common.Authorization;
    }
    return config;
  }

  const token = getAuthToken();
  if (!token) return config;

  config.headers.Authorization = `Bearer ${token}`;
  return config;
};

/** Keeps axios default Authorization in sync with the wallet session (main + stables). */
export const syncHttpAuthorization = (client, token) => {
  if (!client?.defaults?.headers) return;

  if (typeof token === "string" && token.trim()) {
    client.defaults.headers.common.Authorization = `Bearer ${token.trim()}`;
    return;
  }

  delete client.defaults.headers.common.Authorization;
};
